import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ReactFormGenerator } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
import {
  Box,
  Container,
  Modal,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { formApi } from '../../services';
import { useEmbedHeightReporter } from '../../hooks/useEmbedHeightReporter';
import { FormRendererProps, Form } from '../../types';
import {
  generateThemeCSS,
  generateLayoutCSS,
  backgroundSx,
} from '../../utils/themeUtils';
import { formWidthSx } from '../../utils/formWidth';
import CustomButton from '../CustomBlocks/Button';
import { registerComponent } from '../shared/utils';
import {
  SingleLineTextInput,
  MultiLineTextInput,
} from '../CustomBlocks/TextFieldInput';
import MultiSelectInput from '../CustomBlocks/MultiSelectInput';
import ChipSelectInput from '../CustomBlocks/ChipSelectInput';
import { RadioGroupInput } from '../CustomBlocks/ChoiceGroupInput';
import { FormStepper, getStepperPosition } from '../shared/FormStepper';
import LeadContactDialog from './LeadContactDialog';
import { AppDialog } from '../shared/AppDialog';
import { CreditCardIcon } from '../shared/Icons';
import { Toast } from '../shared/Toast';
import DatePicker from '../CustomBlocks/DatePicker';
import SlotBooking from '../CustomBlocks/SlotBooking';
import PaymentSection from '../CustomBlocks/PaymentSection';
import KycButton from '../CustomBlocks/KycButton';
import { AI_OVERRIDE_PREFIX } from '../CustomBlocks/aiGenerator/aiConfig';
import ESignButton from '../CustomBlocks/ESignButton';
import PhoneInput from '../CustomBlocks/PhoneInput';
import EmailInput from '../CustomBlocks/EmailInput';
import NameInput from '../CustomBlocks/NameInput';
import NumberInput from '../CustomBlocks/NumberInput';
import AddressInput from '../CustomBlocks/AddressInput';
import AiGenerator from '../CustomBlocks/AiGenerator';
import CrmAutocompleteInput from '../CustomBlocks/CrmAutocompleteInput';
import WorkflowPreviewBlock from '../CustomBlocks/WorkflowPreviewBlock';
import { QUIZ_BLOCK_COMPONENTS } from '../CustomBlocks/quiz';
import { SCALE_BLOCK_COMPONENTS } from '../CustomBlocks/scale';
import { FormRuntimeContext } from './FormRuntimeContext';
import { resolveCustomValues } from './customFieldValues';
import {
  DateInput,
  TimeInput,
  DateTimeLocalInput,
  MonthYearInput,
} from '../CustomBlocks/DateTimeInput';
import OtpVerificationDialog from './OtpVerificationDialog';
import { fillTemplate } from '../../utils/templateText';
import { PaymentSubmissionContext } from './PaymentSubmissionContext';
import {
  CompletedPaymentDetails,
  PaymentFieldStatusState,
  PreparedPaymentCustomer,
} from './PaymentSubmissionContext';
import { KycSubmissionContext } from './KycSubmissionContext';
import {
  CompletedKycDetails,
  KycFieldStatusState,
} from './KycSubmissionContext';
import { EsignSubmissionContext } from './EsignSubmissionContext';
import {
  CompletedEsignDetails,
  EsignFieldStatusState,
} from './EsignSubmissionContext';

const LEAD_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'state',
] as const;
const REQUIRED_LEAD_FIELDS = [
  'email',
  'phone',
  'state',
] as const;
type LeadField = (typeof LEAD_FIELDS)[number];
type LeadContact = Record<LeadField, string>;

const emptyLeadContact = (): LeadContact => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  state: '',
});

const FormRenderer: React.FC<FormRendererProps> = ({
  tenantId,
  formId,
}) => {
  const [formData, setFormData] = useState<any[]>([]);
  const [form, setForm] = useState<Form | null>(null);

  // The form's custom fields as this browser resolves them (default, or the URL
  // value when enabled).
  const customValues = useMemo(
    () => resolveCustomValues(form?.customFields, window.location.search),
    [form?.customFields],
  );

  // The answers entered so far (earlier steps, plus what is on screen now), keyed
  // by field name. Read by the Workflow Preview block.
  const getWorkflowAnswers = () =>
    buildNameKeyedPayload({ ...formValuesRef.current, ...collectFormData() });

  const { crmTenantId, crmJwtToken } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedTenant = params.get('crmTenantId')?.trim() || '';
    return {
      crmTenantId: /^[a-f\d]{24}$/i.test(requestedTenant) ? requestedTenant : tenantId,
      crmJwtToken: params.get('crmJwtToken')?.trim() || undefined,
    };
  }, [tenantId]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // What the full-screen submit overlay says while `submitting` is true.
  const [submittingLabel, setSubmittingLabel] = useState('Submitting your response…');
  const [formValues, setFormValues] = useState<any>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] =
    useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error'
  >('success');
  const [submitted, setSubmitted] = useState(false);
  const [leadDialogOpen, setLeadDialogOpen] =
    useState(false);
  const [leadContact, setLeadContact] =
    useState<LeadContact>(emptyLeadContact);
  const [leadDialogFields, setLeadDialogFields] = useState<
    LeadField[]
  >([]);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpFields, setOtpFields] = useState<
    { fieldName: string; label: string; contact: string }[]
  >([]);
  const [otpCodes, setOtpCodes] = useState<Record<string, string[]>>({});
  const [otpError, setOtpError] = useState('');
  const [verifiedOtpFields, setVerifiedOtpFields] = useState<
    Record<string, boolean>
  >({});
  const otpResolver = useRef<{
    resolve: () => void;
    reject: (reason?: any) => void;
  } | null>(null);
  const [completedPayments, setCompletedPayments] =
    useState<Record<string, boolean>>({});
  const [paymentStatusMap, setPaymentStatusMap] = useState<
    Record<string, PaymentFieldStatusState>
  >({});
  const [kycStatusMap, setKycStatusMap] = useState<
    Record<string, KycFieldStatusState>
  >({});
  const [esignStatusMap, setEsignStatusMap] = useState<
    Record<string, EsignFieldStatusState>
  >({});
  // Tracks a payment that sessionStorage says is done but the form hasn't
  // been submitted yet - true across reloads too, unlike `completedPayments`
  // (which only gets set by the in-memory checkout flow completing).
  const [hasPendingCompletedPayment, setHasPendingCompletedPayment] =
    useState(false);
  const [resumePaymentDialogOpen, setResumePaymentDialogOpen] =
    useState(false);
  const [resumePaymentStep, setResumePaymentStep] = useState<number | null>(
    null,
  );
  // const [submissionResponse, setSubmissionResponse] =
  //   useState<any>({});
  const formRef = useRef<HTMLFormElement>(null);
  // Custom blocks are hydrated once from persisted form JSON, so their callbacks
  // must read current values instead of the render in which they were attached.
  const formDataRef = useRef<any[]>([]);
  const formValuesRef = useRef<any>({});
  const completedPaymentDetailsRef =
    useRef<CompletedPaymentDetails | null>(null);
  const completedKycDetailsRef =
    useRef<CompletedKycDetails | null>(null);
  const completedEsignDetailsRef =
    useRef<CompletedEsignDetails | null>(null);
  /** Caches the single CRM lead created for this form session so Payment, KYC, and eSign all reuse it instead of creating separate leads. */
  const preparedLeadRef =
    useRef<Promise<PreparedPaymentCustomer> | null>(null);
  const leadContactResolver = useRef<{
    resolve: (value: LeadContact) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  formDataRef.current = formData;
  formValuesRef.current = formValues;

  // A submit creates a response, so it must never run twice (a remounted payment block
  // can ask to auto-advance again, and buttons can be double-clicked).
  const submitInFlightRef = useRef(false);
  const submittedOnceRef = useRef(false);
  const handleSubmitRef = useRef<(event?: any) => any>(() => undefined);
  const handleNextRef = useRef<() => Promise<void> | void>(() => undefined);
  const handlePreviousRef = useRef<() => void>(() => undefined);
  const submitCurrentStepRef = useRef<() => Promise<void> | void>(
    () => undefined,
  );
  const stableHandleSubmit = useRef((event?: any) =>
    handleSubmitRef.current(event),
  ).current;
  const stableHandleNext = useRef(() => handleNextRef.current()).current;
  const stableHandlePrevious = useRef(() =>
    handlePreviousRef.current(),
  ).current;
  const stableSubmitCurrentStep = useRef(() =>
    submitCurrentStepRef.current(),
  ).current;

  // Persists in-progress answers + current step so a full-page redirect (eSign/MITC
  // signing navigates away via window.location.href and comes back on a fresh mount)
  // doesn't wipe out what the user already filled in.
  const formStateStorageKey = `formState:${tenantId || 'unknown'}:${formId || 'unknown'}`;
  // "Submit every step" posts after each step; the id of the response the first step
  // created is sent back so later steps update it instead of creating more. Kept in
  // sessionStorage so a payment/eSign redirect mid-form doesn't start a second one.
  const responseIdStorageKey = `formResponseId:${tenantId || 'unknown'}:${formId || 'unknown'}`;
  const loadResponseId = (): string | undefined => {
    try {
      return sessionStorage.getItem(responseIdStorageKey) || undefined;
    } catch {
      return undefined;
    }
  };
  const saveResponseId = (response: any) => {
    const id = response?.id || response?._id;
    if (!id) return;
    try {
      sessionStorage.setItem(responseIdStorageKey, String(id));
    } catch {}
  };
  const restoredFormStateRef = useRef(false);
  // Distinct from `submitted` (which flips true after every step when
  // submitEveryStep is on) - this only flips once the whole form is done,
  // so intermediate-step answers keep getting persisted in that mode.
  const formFinalizedRef = useRef(false);

  const saveFormState = (values: any, step: number) => {
    try {
      sessionStorage.setItem(
        formStateStorageKey,
        JSON.stringify({ values, step }),
      );
    } catch {}
  };

  // Payment/KYC/eSign all share one CRM lead per form session (see preparePayment
  // below). eSign/MITC redirects away and back on a fresh mount, so the in-memory
  // cache alone isn't enough - persist the prepared lead here too.
  const preparedLeadStorageKey = `formLead:${tenantId || 'unknown'}:${formId || 'unknown'}`;

  const clearFormState = () => {
    formFinalizedRef.current = true;
    try {
      sessionStorage.removeItem(formStateStorageKey);
      sessionStorage.removeItem(preparedLeadStorageKey);
      sessionStorage.removeItem(responseIdStorageKey);
    } catch {}
  };

  const savePreparedLead = (lead: PreparedPaymentCustomer) => {
    try {
      sessionStorage.setItem(
        preparedLeadStorageKey,
        JSON.stringify(lead),
      );
    } catch {}
  };

  const loadPreparedLead =
    (): PreparedPaymentCustomer | null => {
      try {
        const raw = sessionStorage.getItem(
          preparedLeadStorageKey,
        );
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.leadId === 'string') {
          return parsed;
        }
      } catch {}
      return null;
    };

  const isMultiStep = form?.formType === 'multistep';
  const steps = form?.steps || [];
  registerComponent('CustomButton', CustomButton);
  registerComponent('DatePicker', DatePicker);
  registerComponent('SlotBooking', SlotBooking);
  registerComponent('PaymentSection', PaymentSection);
  registerComponent('KycButton', KycButton);
  registerComponent('ESignButton', ESignButton);
  registerComponent('PhoneInput', PhoneInput);
  registerComponent('EmailInput', EmailInput);
  registerComponent('NameInput', NameInput);
  registerComponent('NumberInput', NumberInput);
  registerComponent('AddressInput', AddressInput);
  registerComponent('DateInput', DateInput);
  registerComponent('TimeInput', TimeInput);
  registerComponent('DateTimeInput', DateTimeLocalInput);
  registerComponent('MonthYearInput', MonthYearInput);
  registerComponent('TextInput', SingleLineTextInput);
  registerComponent('TextArea', MultiLineTextInput);
  registerComponent('Tags', MultiSelectInput);
  registerComponent('ChipSelect', ChipSelectInput);
  registerComponent('RadioButtons', RadioGroupInput);
  registerComponent('AiGenerator', AiGenerator);
  registerComponent('CrmAutocomplete', CrmAutocompleteInput);
  registerComponent('WorkflowPreview', WorkflowPreviewBlock);
  Object.entries({ ...QUIZ_BLOCK_COMPONENTS, ...SCALE_BLOCK_COMPONENTS }).forEach(
    ([key, component]) => registerComponent(key, component),
  );

  useEffect(() => {
    loadForm();
  }, [formId]);

  // Warn before an accidental tab close/reload if a payment has gone through but the
  // form (and thus the payment's linked submission) hasn't been finalized yet.
  // `completedPayments` only reflects the in-memory checkout flow completing, so it
  // resets on reload - `hasPendingCompletedPayment` (sourced from sessionStorage) is
  // what keeps this working after a reload.
  useEffect(() => {
    const hasUnsubmittedPayment =
      !submitted &&
      (Object.values(completedPayments).some(Boolean) ||
        hasPendingCompletedPayment);
    if (!hasUnsubmittedPayment) return;

    const handleBeforeUnload = (
      event: BeforeUnloadEvent,
    ) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener(
      'beforeunload',
      handleBeforeUnload,
    );
    return () =>
      window.removeEventListener(
        'beforeunload',
        handleBeforeUnload,
      );
  }, [completedPayments, submitted, hasPendingCompletedPayment]);

  // Optional post-submit file (brochure, price list). When the author asked for
  // it to start on its own, a hidden anchor click fires once the success screen
  // is up. Guarded by a ref because `submitted` flips true after every step of a
  // multi-step form, and the file should be pulled exactly once per submission.
  const autoDownloadedRef = useRef(false);
  useEffect(() => {
    if (!submitted || submitting) return;
    if (!form?.theme?.successDownloadEnabled) return;
    if (!form?.theme?.successDownloadAuto) return;

    const url = (form?.theme?.successDownloadUrl || '').trim();
    if (!url || autoDownloadedRef.current) return;

    autoDownloadedRef.current = true;
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [
    submitted,
    submitting,
    form?.theme?.successDownloadEnabled,
    form?.theme?.successDownloadAuto,
    form?.theme?.successDownloadUrl,
  ]);

  // Keep sessionStorage in sync so a redirect-based verification step (eSign/MITC)
  // can restore answers + step on return instead of forcing the user to refill them.
  useEffect(() => {
    if (!restoredFormStateRef.current || formFinalizedRef.current) return;
    saveFormState(formValues, currentStep);
  }, [formValues, currentStep]);

  const loadForm = async () => {
    try {
      const loadedForm = await formApi.getForm(
        tenantId,
        formId,
      );
      setForm(loadedForm);

      if (loadedForm && loadedForm.data) {
        let formDataArray: any[] = [];

        if (Array.isArray(loadedForm.data)) {
          formDataArray = loadedForm.data;
        } else if (
          typeof loadedForm.data === 'object' &&
          loadedForm.data !== null
        ) {
          if (
            loadedForm.data.task_data &&
            Array.isArray(loadedForm.data.task_data)
          ) {
            formDataArray = loadedForm.data.task_data;
          } else if (
            loadedForm.data.data &&
            Array.isArray(loadedForm.data.data)
          ) {
            formDataArray = loadedForm.data.data;
          } else {
            const keys = Object.keys(
              loadedForm.data,
            ).filter((key) => !isNaN(Number(key)));
            if (keys.length > 0) {
              formDataArray = keys
                .sort((a, b) => parseInt(a) - parseInt(b))
                .map((key) => loadedForm.data[key]);
            }
          }
        }

        const isSubmitEveryStepLocal =
          loadedForm?.formType === 'multistep' &&
          loadedForm?.submitEveryStep;

        const modifiedFormData = formDataArray.map(
          (field: any) => {
            if (
              field.key === 'CustomButton' &&
              field.data
            ) {
              const dataCopy = { ...field.data };
              const type = (
                dataCopy.type ||
                dataCopy?.data?.type ||
                ''
              )
                .toString()
                .toLowerCase();
              if (typeof dataCopy.onSubmitCallback === 'string' && dataCopy.onSubmitCallback.trim() && (type === 'submit' || type === 'gotowebinar')) {
                const customCb = dataCopy.onSubmitCallback;
                dataCopy.onSubmitCallback = async () => {
                  const ok = await stableHandleSubmit();
                  if (ok !== false) new Function('data', customCb)((window as any).data || {});
                  return ok;
                };
              } else if (!dataCopy.onSubmitCallback) {
                if (type === 'submit' || type === 'gotowebinar') {
                  dataCopy.onSubmitCallback = stableHandleSubmit;
                } else if (type === 'nextstep') {
                  dataCopy.onSubmitCallback =
                    isSubmitEveryStepLocal
                      ? stableSubmitCurrentStep
                      : stableHandleNext;
                } else if (type === 'prevstep') {
                  dataCopy.onSubmitCallback =
                    stableHandlePrevious;
                }
              }

              return {
                ...field,
                data: dataCopy,
              };
            }
            if (field.key === 'AiGenerator') {
              // Derived fresh on every render rather than trusted from what was
              // saved: `bare` (which tells react-form-builder2's CustomElement
              // to skip its own field-label wrapper) used to be tied to the
              // removed "description from another field" mode, so an older
              // saved field could still carry bare=true. The field always
              // renders its own textarea and label now — never bare.
              return {
                ...field,
                bare: false,
              };
            }
            if (
              field.key === 'PaymentSection' ||
              field.key === 'KycButton' ||
              field.key === 'ESignButton'
            ) {
              return {
                ...field,
                data: {
                  ...(field.data || {}),
                  data: {
                    ...(field.data?.data || {}),
                    preparePayment,
                  },
                },
              };
            }
            return field;
          },
        );

        setFormData(modifiedFormData);

        try {
          const rawState = sessionStorage.getItem(
            formStateStorageKey,
          );
          if (rawState) {
            const parsedState = JSON.parse(rawState);
            if (parsedState && typeof parsedState === 'object') {
              if (
                parsedState.values &&
                typeof parsedState.values === 'object'
              ) {
                setFormValues(parsedState.values);
              }
              if (typeof parsedState.step === 'number') {
                setCurrentStep(parsedState.step);
              }
            }
          }
        } catch {}
        restoredFormStateRef.current = true;

        const pendingPaymentField = modifiedFormData.find((field: any) => {
          if (field?.key !== 'PaymentSection') return false;
          const fieldName =
            field.data?.data?.field_name || field.field_name || 'payment';
          return (
            sessionStorage.getItem(
              `formPayment:${tenantId || 'unknown'}:${fieldName}:done`,
            ) === '1'
          );
        });
        if (pendingPaymentField) {
          setHasPendingCompletedPayment(true);
          setResumePaymentStep(
            typeof pendingPaymentField.step === 'number'
              ? pendingPaymentField.step
              : null,
          );
          setResumePaymentDialogOpen(true);
        }
      } else {
        setFormData([]);
      }
    } catch (error) {
      setFormData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (data: any) => {
    setFormValues((prev: any) => {
      const next = { ...prev, ...data };
      try {
        (window as any).data = buildNameKeyedPayload(next);
      } catch {}
      return next;
    });
  };

  const collectFormData = () => {
    const formElement =
      formRef.current?.querySelector('form');
    const collectedData: any = { ...formValuesRef.current };

    if (formElement) {
      const formData = new FormData(formElement);

      const jsonValueNames = new Set<string>(
        Array.from(
          formElement.querySelectorAll<HTMLInputElement>('[data-value-json="true"]'),
        )
          .map((el) => el.name || el.id)
          .filter(Boolean),
      );

      const decode = (name: string, value: any) => {
        if (!jsonValueNames.has(name) || typeof value !== 'string') return value;
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      };

      for (const [key, value] of formData.entries()) {
        collectedData[key] = decode(key, value);
      }

      const inputs = formElement.querySelectorAll(
        'input, textarea, select',
      );
      inputs.forEach((input: any) => {
        const name = input.name || input.id;
        if (name) {
          if (jsonValueNames.has(name)) {
            collectedData[name] = decode(name, input.value);
          } else if (input.type === 'checkbox') {
            if (input.checked) {
              if (!collectedData[name])
                collectedData[name] = [];
              if (Array.isArray(collectedData[name])) {
                collectedData[name].push(input.value);
              } else {
                collectedData[name] = [
                  collectedData[name],
                  input.value,
                ];
              }
            }
          } else if (input.type === 'radio') {
            if (input.checked) {
              collectedData[name] = input.value;
            }
          } else {
            collectedData[name] = input.value;
          }
        }
      });
    }

    try {
      const win = window as any;
      const nameKeyed =
        buildNameKeyedPayload(collectedData);
      win.data = nameKeyed;
    } catch (e) {}

    return collectedData;
  };

  const replaceTemplatePlaceholders = fillTemplate;

  const buildTemplateContext = (responseData: any) => {
    const flat: Record<string, any> = {};
    const assignDefined = (src: any) => {
      if (!src || typeof src !== 'object') return;
      Object.keys(src).forEach((k) => {
        const v = src[k];
        if (v !== undefined && v !== null && v !== '')
          flat[k] = v;
      });
    };

    try {
      new URLSearchParams(window.location.search).forEach(
        (v, k) => {
          if (v !== '') flat[k] = v;
        },
      );
    } catch (e) {}

    const nested =
      responseData &&
      typeof responseData.data === 'object' &&
      responseData.data !== null
        ? responseData.data
        : undefined;
    assignDefined(nested);
    assignDefined(responseData);

    const inferredId =
      (responseData &&
        (responseData.id ?? responseData._id)) ??
      (nested && (nested.id ?? nested._id));
    if (inferredId !== undefined && inferredId !== null)
      flat.id = inferredId;

    return { ...flat, data: { ...flat } };
  };

  const resolveFieldHrefs = (
    fields: any[],
    responseData: any,
    templateContext: any,
  ) => {
    const decodeTemplate = (value: string) => {
      let decoded = value;
      for (let i = 0; i < 4; i++) {
        try {
          const next = decodeURIComponent(decoded);
          if (next === decoded) break;
          decoded = next;
        } catch (e) {
          break;
        }
      }
      return /\{\{/.test(decoded) ? decoded : value;
    };

    return (fields || []).map((field: any) => {
      try {
        if (!field) return field;
        const newField = { ...field };

        if (
          newField.data &&
          typeof newField.data === 'object'
        ) {
          newField.data = {
            ...newField.data,
            submissionResponse: responseData,
          };

          const existingTemplate =
            newField.data.hrefTemplate ?? null;
          const href = newField.data.href ?? null;
          let template = existingTemplate
            ? String(existingTemplate)
            : typeof href === 'string'
              ? decodeTemplate(String(href))
              : null;

          if (template && /\{\{/.test(template)) {
            newField.data.hrefTemplate = template;
            newField.data.href =
              replaceTemplatePlaceholders(
                template,
                templateContext,
              );
          }
        }

        const topTemplate = newField.hrefTemplate ?? null;
        const topHref = newField.href ?? null;
        const resolvedTop = topTemplate
          ? String(topTemplate)
          : typeof topHref === 'string'
            ? decodeTemplate(String(topHref))
            : null;
        if (resolvedTop && /\{\{/.test(resolvedTop)) {
          newField.hrefTemplate = resolvedTop;
          newField.href = replaceTemplatePlaceholders(
            resolvedTop,
            templateContext,
          );
        }

        return newField;
      } catch (e) {
        return field;
      }
    });
  };

  const buildNameKeyedPayload = (rawData: any) => {
    const payload: Record<string, any> = {};
    try {
      const fd = Array.isArray(formDataRef.current)
        ? formDataRef.current
        : [];

      fd.forEach((f: any) => {
        if (!f) return;
        const preferredKey =
          f.field_name && String(f.field_name).trim() !== ''
            ? f.field_name
            : f.name || f.id;
        const candidates = [f.field_name, f.name, f.id]
          .filter(Boolean)
          .map(String);
        let val: any = undefined;

        for (const c of candidates) {
          if (
            rawData &&
            Object.prototype.hasOwnProperty.call(rawData, c)
          ) {
            val = rawData[c];
            break;
          }
        }
        if (
          val === undefined &&
          f.id &&
          rawData &&
          Object.prototype.hasOwnProperty.call(
            rawData,
            f.id,
          )
        ) {
          val = rawData[f.id];
        }

        if (val !== undefined) {
          payload[preferredKey] = val;
        }
      });

      // Reserved `_aiCfg:<field_name>` keys carry each AiGenerator field's
      // effective config for workflow variables — not form fields, so they are
      // copied across by prefix instead of looked up by field name.
      if (rawData && typeof rawData === 'object') {
        Object.entries(rawData).forEach(([key, value]) => {
          if (
            key.startsWith(AI_OVERRIDE_PREFIX) &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value)
          ) {
            payload[key] = value;
          }
        });
      }

      return payload;
    } catch (e) {
      return rawData;
    }
  };

  // Lets a verification block (e.g. KycButton's PAN/DOB) reuse a value already
  // captured by a regular form field instead of asking the user to re-enter it,
  // matched loosely by field name since we don't know the builder's exact key.
  const findFormFieldValue = (
    keywords: string[],
  ): string | undefined => {
    try {
      // formValuesRef alone can be stale - some field types in the underlying
      // form library don't fire onChange promptly, so pull fresh DOM values
      // the same way validation/payment prep do before searching.
      const collected = collectFormData();
      const merged = { ...formValuesRef.current, ...collected };
      const payload = buildNameKeyedPayload(merged);
      const normalize = (s: string) =>
        s.toLowerCase().replace(/[^a-z0-9]/g, '');
      const entry = Object.entries(payload).find(
        ([key, value]) =>
          typeof value === 'string' &&
          value.trim() !== '' &&
          keywords.some((kw) => normalize(key).includes(kw)),
      );
      return entry ? String(entry[1]) : undefined;
    } catch {
      return undefined;
    }
  };

  const getFieldsForCurrentStep = () => {
    if (!isMultiStep || !steps || steps.length === 0) {
      return formData;
    }

    return (formData || []).filter((field: any) => {
      if (!field) return false;
      const stepVal = field.step;
      if (stepVal === undefined || stepVal === null)
        return false;
      return Number(stepVal) === Number(currentStep);
    });
  };

  const validateRequiredFields = (
    fields: any[],
    collectedData: any,
    formElement: HTMLFormElement | null,
  ): string | null => {
    for (const field of fields) {
      const isRequired =
        field.required === true ||
        field.required === 'true' ||
        field.required === 'True';

      if (!isRequired) continue;

      const fieldName =
        field.field_name || field.name || field.id;
      if (!fieldName) continue;

      let hasValue = false;

      // Check in collected data first
      if (
        collectedData &&
        collectedData[fieldName] !== undefined &&
        collectedData[fieldName] !== null
      ) {
        const value = collectedData[fieldName];
        if (Array.isArray(value)) {
          hasValue =
            value.length > 0 &&
            value.some(
              (v: any) => v && v.toString().trim() !== '',
            );
        } else if (typeof value === 'string') {
          hasValue = value.trim() !== '';
        } else {
          hasValue = true; // Has some value
        }
      }

      // If still no value, check the DOM
      if (!hasValue && formElement) {
        const input = formElement.querySelector(
          `[name="${fieldName}"], [id="${fieldName}"]`,
        ) as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement;

        if (input) {
          if (input.type === 'checkbox') {
            const checkboxes = formElement.querySelectorAll(
              `input[type='checkbox'][name="${fieldName}"]`,
            ) as NodeListOf<HTMLInputElement>;
            if (checkboxes && checkboxes.length > 0) {
              hasValue = Array.from(checkboxes).some(
                (cb) => cb.checked,
              );
            } else {
              hasValue = (input as HTMLInputElement)
                .checked;
            }
          } else if (input.type === 'radio') {
            const radioGroup = formElement.querySelectorAll(
              `[name="${fieldName}"]`,
            ) as NodeListOf<HTMLInputElement>;
            hasValue = Array.from(radioGroup).some(
              (radio) => radio.checked,
            );
          } else {
            const value = input.value;
            hasValue =
              value !== null &&
              value !== undefined &&
              String(value).trim() !== '';
          }
        }
      }

      // Final fallback: check form groups by label
      if (!hasValue && formElement) {
        try {
          const labelText = (
            field.label ||
            field.text ||
            fieldName ||
            ''
          )
            .toString()
            .trim();
          if (labelText) {
            const groups = Array.from(
              formElement.querySelectorAll('.form-group'),
            ) as HTMLElement[];
            for (const g of groups) {
              const lbl = g.querySelector('label');
              if (!lbl) continue;

              const lblClone = lbl.cloneNode(
                true,
              ) as HTMLElement;
              const badges = Array.from(
                lblClone.querySelectorAll(
                  '.label-required, .badge, .label-danger',
                ),
              );
              badges.forEach((b) => b.remove());
              const txt = (
                lblClone.textContent || ''
              ).trim();

              if (!txt) continue;
              if (
                txt === labelText ||
                txt.startsWith(labelText) ||
                labelText.startsWith(txt)
              ) {
                // Check checkboxes
                const cbs = Array.from(
                  g.querySelectorAll(
                    'input[type="checkbox"]',
                  ),
                ) as HTMLInputElement[];
                if (cbs.length > 0) {
                  hasValue = cbs.some((cb) => cb.checked);
                  if (hasValue) break;
                }

                // Check radio buttons
                const rds = Array.from(
                  g.querySelectorAll('input[type="radio"]'),
                ) as HTMLInputElement[];
                if (rds.length > 0) {
                  hasValue = rds.some((r) => r.checked);
                  if (hasValue) break;
                }

                const hiddenValueInput = g.querySelector(
                  `input[type="hidden"][name="${fieldName}"]`,
                ) as HTMLInputElement | null;
                const otherInput =
                  hiddenValueInput ||
                  (g.querySelector(
                    'input:not([type=checkbox]):not([type=radio]):not([type=hidden]), textarea, select',
                  ) as
                    | HTMLInputElement
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                    | null);
                if (otherInput) {
                  const v = (otherInput as any).value;
                  hasValue =
                    v !== null &&
                    v !== undefined &&
                    String(v).trim() !== '';
                  if (hasValue) break;
                }
              }
            }
          }
        } catch (e) {
          // ignore fallback errors
        }
      }

      // If still no value, return error
      if (!hasValue) {
        const label =
          field.label || field.text || fieldName;
        return `Please fill in the required field: ${label}`;
      }
    }

    return null;
  };

  const requestOtpVerification = (
    allFormData: Record<string, any>,
    scopeFields?: any[],
  ): Promise<void> => {
    const pending = (scopeFields || formDataRef.current || [])
      .filter(
        (field: any) =>
          (field?.key === 'EmailInput' || field?.key === 'PhoneInput') &&
          Boolean(field?.requireOtp),
      )
      .map((field: any) => {
        const fieldName = field.field_name;
        return {
          fieldName,
          label: field.key === 'EmailInput' ? 'email' : 'phone number',
          contact: fieldName ? String(allFormData[fieldName] || '') : '',
        };
      })
      .filter(
        (entry) =>
          entry.fieldName &&
          entry.contact &&
          !verifiedOtpFields[entry.fieldName],
      );

    if (pending.length === 0) return Promise.resolve();

    setOtpFields(pending);
    setOtpCodes(
      pending.reduce(
        (acc, entry) => ({
          ...acc,
          [entry.fieldName]: Array(6).fill(''),
        }),
        {} as Record<string, string[]>,
      ),
    );
    setOtpError('');
    setOtpDialogOpen(true);
    return new Promise<void>((resolve, reject) => {
      otpResolver.current = { resolve, reject };
    });
  };

  const cancelOtpVerification = () => {
    otpResolver.current?.reject(
      new Error('OTP verification cancelled.'),
    );
    otpResolver.current = null;
    setOtpDialogOpen(false);
  };

  const confirmOtpVerification = () => {
    const incomplete = otpFields.some(
      (entry) =>
        (otpCodes[entry.fieldName] || []).some((digit) => !digit) ||
        (otpCodes[entry.fieldName] || []).length < 6,
    );
    if (incomplete) {
      setOtpError('Please enter the full 6-digit OTP for each field.');
      return;
    }
    setVerifiedOtpFields((current) => {
      const next = { ...current };
      otpFields.forEach((entry) => {
        next[entry.fieldName] = true;
      });
      return next;
    });
    otpResolver.current?.resolve();
    otpResolver.current = null;
    setOtpDialogOpen(false);
  };

  const requestLeadContact = (
    initial: LeadContact,
  ): Promise<LeadContact> => {
    const missing =
      REQUIRED_LEAD_FIELDS.some(
        (field) => !initial[field].trim(),
      ) || !initial.firstName.trim();
    if (!missing) return Promise.resolve(initial);

    setLeadContact(initial);
    setLeadDialogFields(
      LEAD_FIELDS.filter((field) => !initial[field].trim()),
    );
    setLeadDialogOpen(true);
    return new Promise<LeadContact>((resolve, reject) => {
      leadContactResolver.current = { resolve, reject };
    });
  };

  const cancelLeadContact = () => {
    leadContactResolver.current?.reject(
      new Error(
        'Payment cancelled. Lead contact details are required.',
      ),
    );
    leadContactResolver.current = null;
    setLeadDialogOpen(false);
  };

  const confirmLeadContact = () => {
    const missing = REQUIRED_LEAD_FIELDS.find(
      (field) => !leadContact[field].trim(),
    );
    if (missing) {
      setSnackbarMessage(
        `Please provide the lead ${missing}.`,
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!leadContact.firstName.trim()) {
      setSnackbarMessage('Please provide a first name.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    leadContactResolver.current?.resolve(
      LEAD_FIELDS.reduce((result, field) => {
        result[field] = leadContact[field].trim();
        return result;
      }, emptyLeadContact()),
    );
    leadContactResolver.current = null;
    setLeadDialogOpen(false);
  };

  /**
   * Called by PaymentSection/KycButton/ESignButton before they can proceed.
   * Creates the CRM lead once per form session and caches the in-flight/resolved
   * promise so Payment, KYC, and eSign all reuse the same lead instead of each
   * creating its own.
   */
  const preparePayment = async () => {
    if (preparedLeadRef.current) {
      return preparedLeadRef.current;
    }
    const storedLead = loadPreparedLead();
    if (storedLead) {
      const resolved = Promise.resolve(storedLead);
      preparedLeadRef.current = resolved;
      return resolved;
    }
    const promise = createPaymentLead();
    preparedLeadRef.current = promise;
    try {
      const lead = await promise;
      savePreparedLead(lead);
      return lead;
    } catch (error) {
      preparedLeadRef.current = null;
      throw error;
    }
  };

  const createPaymentLead = async () => {
    const formElement =
      formRef.current?.querySelector('form') || null;
    const collectedData = collectFormData();
    const allFormData = {
      ...formValuesRef.current,
      ...collectedData,
    };
    const validationError = validateRequiredFields(
      formDataRef.current,
      allFormData,
      formElement,
    );
    if (validationError) {
      setSnackbarMessage(validationError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw new Error(validationError);
    }

    const initialLead = LEAD_FIELDS.reduce(
      (result, field) => {
        const value = allFormData[field];
        result[field] =
          value === undefined || value === null
            ? ''
            : String(value);
        return result;
      },
      emptyLeadContact(),
    );
    const completeLead =
      await requestLeadContact(initialLead);
    const completeFormData = {
      ...allFormData,
      ...completeLead,
    };
    const payload = {
      ...buildNameKeyedPayload(completeFormData),
      ...completeLead,
    };

    setFormValues(completeFormData);
    const result = await formApi.submitForPayment(
      tenantId,
      formId,
      payload,
      completeLead,
    );

    return {
      leadId: result.leadId,
      formId,
      name: `${completeLead.firstName} ${completeLead.lastName}`.trim(),
      email: completeLead.email,
      phone: completeLead.phone,
    };
  };

  const completePayment = async (
    fieldName: string,
    payment: CompletedPaymentDetails,
  ) => {
    setCompletedPayments((current) => ({
      ...current,
      [fieldName]: true,
    }));
    completedPaymentDetailsRef.current = payment;
  };

  const advanceToNextStep = () => {
    if (!isMultiStep || submitting) return;
    if (currentStep >= steps.length - 1) return;
    if (form?.submitEveryStep) {
      stableSubmitCurrentStep();
    } else {
      stableHandleNext();
    }
  };

  const advanceAfterPayment = () => {
    if (!isMultiStep || !steps || steps.length === 0) {
      stableHandleSubmit();
      return;
    }
    if (form?.submitEveryStep) {
      stableSubmitCurrentStep();
      return;
    }
    if (currentStep < steps.length - 1) {
      stableHandleNext();
    } else {
      stableHandleSubmit();
    }
  };

  /**
   * PaymentSection's own async checkout flow can outlive its component instance:
   * the step-change key on ReactFormGenerator remounts field components whenever
   * formValues changes (e.g. preparePayment syncing DOM values into state), which
   * would otherwise reset PaymentSection's local processing/done status mid-flight.
   * Lifting status here keeps it alive across those remounts.
   */
  const getPaymentStatus = (fieldName: string) =>
    paymentStatusMap[fieldName];
  const setPaymentStatus = (
    fieldName: string,
    state: PaymentFieldStatusState,
  ) => {
    setPaymentStatusMap((current) => ({
      ...current,
      [fieldName]: state,
    }));
  };

  const [completedKyc, setCompletedKyc] = useState<Record<string, boolean>>({});
  const completeKyc = async (
    fieldName: string,
    kyc: CompletedKycDetails,
  ) => {
    setCompletedKyc((current) => ({ ...current, [fieldName]: true }));
    completedKycDetailsRef.current = kyc;
  };
  const getKycStatus = (fieldName: string) => kycStatusMap[fieldName];
  const setKycStatus = (
    fieldName: string,
    state: KycFieldStatusState,
  ) => {
    setKycStatusMap((current) => ({ ...current, [fieldName]: state }));
  };

  const [completedEsigns, setCompletedEsigns] = useState<Record<string, boolean>>({});
  const completeEsign = async (
    fieldName: string,
    esign: CompletedEsignDetails,
  ) => {
    setCompletedEsigns((current) => ({ ...current, [fieldName]: true }));
    completedEsignDetailsRef.current = esign;
  };
  const getEsignStatus = (fieldName: string) => esignStatusMap[fieldName];
  const setEsignStatus = (
    fieldName: string,
    state: EsignFieldStatusState,
  ) => {
    setEsignStatusMap((current) => ({ ...current, [fieldName]: state }));
  };

  const getStoredKycDetails = (): CompletedKycDetails | null => {
    for (const field of formDataRef.current.filter(
      (item: any) => item?.key === 'KycButton',
    )) {
      const fieldName =
        field.data?.data?.field_name || field.field_name || 'kyc';
      const raw = sessionStorage.getItem(
        `formKycMeta:${tenantId || 'unknown'}:${fieldName}`,
      );
      if (!raw) continue;
      try {
        const kyc = JSON.parse(raw);
        if (kyc?.fieldName && kyc?.leadId) return kyc;
      } catch {}
    }
    return null;
  };

  const getStoredEsignDetails = (): CompletedEsignDetails | null => {
    for (const field of formDataRef.current.filter(
      (item: any) => item?.key === 'ESignButton',
    )) {
      const fieldName =
        field.data?.data?.field_name || field.field_name || 'esign';
      const raw = sessionStorage.getItem(
        `formEsignMeta:${tenantId || 'unknown'}:${fieldName}`,
      );
      if (!raw) continue;
      try {
        const esign = JSON.parse(raw);
        if (esign?.fieldName && esign?.leadId && esign?.verificationId)
          return esign;
      } catch {}
    }
    return null;
  };

  const getRequiredKycError = () => {
    const requiredKyc = formDataRef.current.find(
      (field: any) =>
        field?.key === 'KycButton' && field?.data?.data?.isRequired,
    );
    if (!requiredKyc) return null;
    const fieldName =
      requiredKyc.data?.data?.field_name || requiredKyc.field_name || 'kyc';
    const storageKey = `formKyc:${tenantId || 'unknown'}:${fieldName}:done`;
    if (!completedKyc[fieldName] && sessionStorage.getItem(storageKey) !== '1') {
      return 'Please complete the required KYC verification before submitting the form.';
    }
    return null;
  };

  const getRequiredEsignError = () => {
    const requiredEsign = formDataRef.current.find(
      (field: any) =>
        field?.key === 'ESignButton' && field?.data?.data?.isRequired,
    );
    if (!requiredEsign) return null;
    const fieldName =
      requiredEsign.data?.data?.field_name || requiredEsign.field_name || 'esign';
    const storageKey = `formEsign:${tenantId || 'unknown'}:${fieldName}:done`;
    if (!completedEsigns[fieldName] && sessionStorage.getItem(storageKey) !== '1') {
      return 'Please complete the required eSign before submitting the form.';
    }
    return null;
  };

  const clearPaymentSessionKeys = () => {
    formDataRef.current
      .filter(
        (field: any) => field?.key === 'PaymentSection',
      )
      .forEach((field: any) => {
        const fieldName =
          field.data?.data?.field_name ||
          field.field_name ||
          'payment';
        sessionStorage.removeItem(
          `formPayment:${tenantId || 'unknown'}:${fieldName}:done`,
        );
        sessionStorage.removeItem(
          `formPaymentMeta:${tenantId || 'unknown'}:${fieldName}`,
        );
        sessionStorage.removeItem(
          `formPaymentAdvanced:${tenantId || 'unknown'}:${fieldName}`,
        );
      });
    setCompletedPayments({});
    setHasPendingCompletedPayment(false);
  };

  // Once the form is submitted, the KYC/eSign verifications tied to this
  // fill-out are done - clear their "done" flags so a later reload/reuse of
  // this browser session for the same form doesn't show them as pre-verified.
  const clearKycEsignSessionKeys = () => {
    formDataRef.current
      .filter((field: any) => field?.key === 'KycButton')
      .forEach((field: any) => {
        const fieldName =
          field.data?.data?.field_name || field.field_name || 'kyc';
        sessionStorage.removeItem(
          `formKyc:${tenantId || 'unknown'}:${fieldName}:done`,
        );
        sessionStorage.removeItem(
          `formKycMeta:${tenantId || 'unknown'}:${fieldName}`,
        );
      });
    formDataRef.current
      .filter((field: any) => field?.key === 'ESignButton')
      .forEach((field: any) => {
        const fieldName =
          field.data?.data?.field_name || field.field_name || 'esign';
        sessionStorage.removeItem(
          `formEsign:${tenantId || 'unknown'}:${fieldName}:done`,
        );
        sessionStorage.removeItem(
          `formEsignMeta:${tenantId || 'unknown'}:${fieldName}`,
        );
        sessionStorage.removeItem(
          `formEsignVerification:${tenantId || 'unknown'}:${fieldName}`,
        );
        sessionStorage.removeItem(
          `formEsignPendingLead:${tenantId || 'unknown'}:${fieldName}`,
        );
      });
    setCompletedKyc({});
    setCompletedEsigns({});
    setKycStatusMap({});
    setEsignStatusMap({});
  };

  const getStoredPaymentDetails =
    (): CompletedPaymentDetails | null => {
      for (const field of formDataRef.current.filter(
        (item: any) => item?.key === 'PaymentSection',
      )) {
        const fieldName =
          field.data?.data?.field_name ||
          field.field_name ||
          'payment';
        const raw = sessionStorage.getItem(
          `formPaymentMeta:${tenantId || 'unknown'}:${fieldName}`,
        );
        if (!raw) continue;
        try {
          const payment = JSON.parse(raw);
          if (
            payment?.leadId &&
            payment?.paymentId &&
            payment?.fieldName
          )
            return payment;
        } catch {}
      }
      return null;
    };

  const getRequiredPaymentError = () => {
    const requiredPayment = formDataRef.current.find(
      (field: any) =>
        field?.key === 'PaymentSection' &&
        field?.data?.data?.isRequired,
    );
    if (!requiredPayment) return null;
    const fieldName =
      requiredPayment.data?.data?.field_name ||
      requiredPayment.field_name ||
      'payment';
    const storageKey = `formPayment:${tenantId || 'unknown'}:${fieldName}:done`;
    if (
      !completedPayments[fieldName] &&
      sessionStorage.getItem(storageKey) !== '1'
    ) {
      return 'Please complete the required payment before submitting the form.';
    }
    return null;
  };

  const handleSubmit = async (
    event?: React.FormEvent | React.MouseEvent,
  ) => {
    // `event` may actually be a submissionResponse/data blob when this is
    // invoked as a CustomButton onSubmitCallback (see Button.tsx), not a
    // real DOM event - only call preventDefault when it really is one.
    if (event && typeof (event as any).preventDefault === 'function') {
      event.preventDefault();
    }
    if (submitInFlightRef.current || submittedOnceRef.current) return false;
    submitInFlightRef.current = true;
    setSubmittingLabel('Submitting your response…');
    setSubmitting(true);
    try {
      const paymentError = getRequiredPaymentError();
      const kycError = getRequiredKycError();
      const esignError = getRequiredEsignError();
      const requiredStepError = paymentError || kycError || esignError;
      if (requiredStepError) {
        setSnackbarMessage(requiredStepError);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false;
      }
      const formElement =
        formRef.current?.querySelector('form');
      const currentStepData = collectFormData();
      const allFormData = {
        ...formValues,
        ...currentStepData,
      };

      let validationError: string | null = null;
      const fieldsToValidate = getFieldsForCurrentStep();
      validationError = validateRequiredFields(
        fieldsToValidate,
        allFormData,
        formElement || null,
      );

      if (validationError) {
        setSnackbarMessage(validationError);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false;
      }

      try {
        await requestOtpVerification(allFormData);
      } catch (otpCancelled) {
        return false;
      }

      setFormValues(allFormData);

      const nameKeyedPayload =
        buildNameKeyedPayload(allFormData);
      try {
        const win = window as any;
        win.data = nameKeyedPayload;
      } catch (e) {}

      const submitResponse =
        await formApi.submitFormResponse(
          tenantId,
          formId,
          nameKeyedPayload,
          completedPaymentDetailsRef.current ||
            getStoredPaymentDetails() ||
            undefined,
          completedKycDetailsRef.current ||
            getStoredKycDetails() ||
            undefined,
          completedEsignDetailsRef.current ||
            getStoredEsignDetails() ||
            undefined,
          loadResponseId(),
        );
      saveResponseId(submitResponse);
      // setSubmissionResponse(submitResponse);
      try {
        const resp = submitResponse;
        const win = window as any;
        win.formSubmissionResponse = resp;
        win.submissionResponse = resp;
        try {
          const existing = win.data || {};
          const merged = { ...existing, ...resp };
          if (
            existing &&
            existing.data &&
            resp &&
            resp.data &&
            typeof existing.data === 'object' &&
            typeof resp.data === 'object'
          ) {
            merged.data = {
              ...existing.data,
              ...resp.data,
            };
          }
          win.data = merged;
        } catch (e2) {
          win.data = resp;
        }
      } catch (e) {
        console.warn(
          'Unable to attach submission response to window',
          e,
        );
      }

      submittedOnceRef.current = true;
      setSubmitted(true);
      clearPaymentSessionKeys();
      clearKycEsignSessionKeys();
      clearFormState();

      try {
        const responseData = submitResponse || {};
        const templateContext =
          buildTemplateContext(responseData);

        const modifiedFormData = resolveFieldHrefs(
          formData || [],
          responseData,
          templateContext,
        );
        setFormData(modifiedFormData);
      } catch (err) {
        console.warn(
          'Error computing injecting href values',
          err,
        );
      }
      try {
        if (isMultiStep && steps && steps.length > 0) {
          const lastIndex = Math.max(0, steps.length - 1);
          setCurrentStep(lastIndex);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (err) {
        console.warn(
          'Error advancing to final step after submit',
          err,
        );
      }
      return true;
    } catch (error: any) {
      setSnackbarMessage(
        error.response?.data?.error ||
          'Error submitting form',
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };
  handleSubmitRef.current = handleSubmit;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleNext = async () => {
    let formElement =
      formRef.current?.querySelector('form');
    let collectedData: any = {};
    if (formElement) {
      collectedData = collectFormData();
    } else {
      collectedData = { ...formValues };
    }
    const mergedData = {
      ...(formValues || {}),
      ...(collectedData || {}),
    };
    let fieldsToValidate: any[] = [];
    if (isMultiStep && steps && steps.length > 0) {
      fieldsToValidate = getFieldsForCurrentStep();
    } else {
      fieldsToValidate = formData;
    }
    const validationError = validateRequiredFields(
      fieldsToValidate,
      mergedData,
      formElement || null,
    );
    if (validationError) {
      setSnackbarMessage(validationError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await requestOtpVerification(mergedData, fieldsToValidate);
    } catch (otpCancelled) {
      return;
    }

    setFormValues((prev: any) => ({
      ...prev,
      ...collectedData,
    }));

    if (
      isMultiStep &&
      steps &&
      steps.length > 0 &&
      currentStep < steps.length - 1
    ) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  handleNextRef.current = handleNext;

  const submitCurrentStep = async () => {
    if (!isMultiStep || currentStep >= steps.length) return;
    setSubmittingLabel('Saving your response…');
    setSubmitting(true);
    try {
      const formElement =
        formRef.current?.querySelector('form');
      const collectedData = collectFormData();

      const mergedData = {
        ...(formValues || {}),
        ...(collectedData || {}),
      };

      const fieldsToValidate = getFieldsForCurrentStep();
      const validationError = validateRequiredFields(
        fieldsToValidate,
        mergedData,
        formElement || null,
      );

      if (validationError) {
        setSnackbarMessage(validationError);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSubmitting(false);
        return;
      }

      setFormValues((prev: any) => ({
        ...prev,
        ...collectedData,
      }));

      const allFormData = {
        ...(formValues || {}),
        ...(collectedData || {}),
      };
      const nameKeyedPayload =
        buildNameKeyedPayload(allFormData);
      try {
        const win = window as any;
        win.data = nameKeyedPayload;
      } catch (e) {}

      const submitResponse =
        await formApi.submitFormResponse(
          tenantId,
          formId,
          nameKeyedPayload,
          completedPaymentDetailsRef.current ||
            getStoredPaymentDetails() ||
            undefined,
          completedKycDetailsRef.current ||
            getStoredKycDetails() ||
            undefined,
          completedEsignDetailsRef.current ||
            getStoredEsignDetails() ||
            undefined,
          loadResponseId(),
        );
      saveResponseId(submitResponse);

      // setSubmissionResponse(submitResponse);
      try {
        const resp = submitResponse;
        const win = window as any;
        win.formSubmissionResponse = resp;
        win.submissionResponse = resp;
        try {
          const existing = win.data || {};
          const merged = { ...existing, ...resp };
          if (
            existing &&
            existing.data &&
            resp &&
            resp.data &&
            typeof existing.data === 'object' &&
            typeof resp.data === 'object'
          ) {
            merged.data = {
              ...existing.data,
              ...resp.data,
            };
          }
          win.data = merged;
        } catch (e2) {
          win.data = resp;
        }
      } catch (e) {}

      try {
        const responseData = submitResponse || {};
        setFormData(
          resolveFieldHrefs(
            formDataRef.current || [],
            responseData,
            buildTemplateContext(responseData),
          ),
        );
      } catch (err) {
        console.warn(
          'Error computing injecting href values',
          err,
        );
      }

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitted(true);
        clearPaymentSessionKeys();
        clearKycEsignSessionKeys();
        clearFormState();
      }
    } catch (error: any) {
      setSnackbarMessage(
        error.response?.data?.error ||
          'Error submitting form',
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };
  submitCurrentStepRef.current = submitCurrentStep;

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  handlePreviousRef.current = handlePrevious;

  useEffect(() => {
    const win = window as any;
    win.__formActions = {
      next: () => {
        try {
          return handleNext();
        } catch (e) {
          return undefined;
        }
      },
      prev: () => {
        try {
          return handlePrevious();
        } catch (e) {
          return undefined;
        }
      },
      submit: () => {
        try {
          return handleSubmit();
        } catch (e) {
          return undefined;
        }
      },
    };
    return () => {
      try {
        delete win.__formActions;
      } catch (e) {}
    };
  }, [currentStep, formValues, submitting]);

  useEmbedHeightReporter(formId, [loading, formData, currentStep, submitted]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight: '100vh',
          px: 4,
        }}
      >
        <CircularProgress sx={{ color: '#4f46e5' }} />
        <Typography variant="body2" sx={{ color: '#8a8a96' }}>
          Loading form...
        </Typography>
      </Box>
    );
  }

  if (!formData || formData.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 60%)',
          px: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 380 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              mx: 'auto',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f1f3f9',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
                stroke="#94a3b8"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 3v4a1 1 0 0 0 1 1h4"
                stroke="#94a3b8"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.5 13.5a2.5 2.5 0 1 1 3.4 2.3c-.6.25-.9.6-.9 1.1"
                stroke="#94a3b8"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="19.2" r="0.9" fill="#94a3b8" />
            </svg>
          </Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1e293b', mb: 0.75 }}>
            This form isn't available
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.6 }}>
            It may have been unpublished, or the link you followed is
            incorrect. Please check the link or contact whoever shared it
            with you.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!submitting && submitted && (!isMultiStep || !form?.submitEveryStep || formFinalizedRef.current)) {
    const successTitle = form?.theme?.successTitle || 'Form Submitted Successfully!';
    const successMessage =
      form?.theme?.successMessage ||
      'Thank you for your time. Your response has been recorded.';
    const successButtonLabel = form?.theme?.successButtonLabel || 'Okay';
    const successDownloadUrl = form?.theme?.successDownloadEnabled
      ? (form?.theme?.successDownloadUrl || '').trim()
      : '';
    const successDownloadLabel =
      (form?.theme?.successDownloadLabel || '').trim() || 'Download brochure';

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #eef5ff 0%, #ffffff 60%)',
          py: 4,
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 460,
            p: { xs: 4, sm: 6 },
            borderRadius: '24px',
            textAlign: 'center',
            backgroundColor: '#ffffff',
            border: 'none',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.10)',
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 20% 15%, rgba(59,130,246,0.10) 0, transparent 40%), radial-gradient(circle at 80% 10%, rgba(59,130,246,0.08) 0, transparent 35%)',
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                backgroundColor: '#f4f8ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                position: 'relative',
              }}
            >
              {['✦', '✦', '●', '■', '▲', '●'].map((mark, i) => {
                const positions = [
                  { top: '6%', left: '14%', size: 14, color: '#60a5fa' },
                  { top: '2%', right: '10%', size: 10, color: '#e2e8f0' },
                  { top: '18%', right: '2%', size: 8, color: '#22c55e' },
                  { bottom: '10%', left: '2%', size: 10, color: '#f472b6' },
                  { top: '10%', left: '0%', size: 10, color: '#3b82f6' },
                  { bottom: '2%', right: '18%', size: 8, color: '#facc15' },
                ];
                const pos = positions[i];
                return (
                  <Box
                    key={i}
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      fontSize: pos.size,
                      color: pos.color,
                      top: pos.top,
                      left: pos.left,
                      right: pos.right,
                      bottom: pos.bottom,
                    }}
                  >
                    {mark}
                  </Box>
                );
              })}
              <Box
                sx={{
                  width: 92,
                  height: 92,
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(34,197,94,0.35)',
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{ width: 44, height: 44 }}
                >
                  <path
                    d="M5 13l4 4L19 7"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>
              </Box>
            </Box>

            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontSize: { xs: 20, sm: 24 } }}
            >
              {successTitle}
            </Typography>
            <Box sx={{ mb: 5,mt:2}}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 5, fontSize: { xs: 14, sm: 15 } }}
            >
              {successMessage}
            </Typography>
            </Box>
        
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {successDownloadUrl && (
    
              <Box
                component="a"
                href={successDownloadUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                  borderRadius: '999px',
                  py: 1.5,
                  px: 4,
                  mt:"10px",
                  fontSize: 15,
                  fontWeight: 600,
                  textDecoration: 'none',
                  border: '1px solid #e2e5ea',
                  color: '#111111',
                  backgroundColor: '#ffffff',
                  transition: 'border-color 0.15s ease, background 0.15s ease',
                  '&:hover': { backgroundColor: '#f7f8fa', borderColor: '#cfd4dc' },
                }}
              >
                <Box component="svg" viewBox="0 0 24 24" sx={{ width: 17, height: 17, opacity: 0.75 }}>
                  <path
                    d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.9}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>
                {successDownloadLabel}
              </Box>
            )}

            <Box
              component="button"
              type="button"
              onClick={() => {
                try {
                  window.location.reload();
                } catch (e) {}
              }}
              sx={{
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '999px',
                py: 1.5,
                px: 4,
                fontSize: 16,
                fontWeight: 600,
                backgroundColor: '#111111',
                color: '#ffffff',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              {successButtonLabel}
            </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  const stepperPosition = getStepperPosition(form?.theme);
  const showStepper =
    isMultiStep && steps.length > 0 && stepperPosition !== 'hidden';

  const rendererThemeCSS =
    generateLayoutCSS('.form-renderer-container') +
    generateThemeCSS(
      form?.theme || null,
      '.form-renderer-container',
      true,
    );

  return (
    <Box
      sx={{
        background:
          form?.theme?.background || '#f5f7fa',
      }}
      className="form-renderer-container"
    >
      <style>{rendererThemeCSS}</style>
      <Container
        maxWidth={false}
        sx={{ px: 0 }}
        className="form-renderer-container"
      >
        {/* The indicator can sit above, below or beside the form, so the two
            are wrapped in one flex container rather than being siblings in a
            fixed order. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: stepperPosition === 'left' ? 'row' : 'column',
            alignItems: stepperPosition === 'left' ? 'flex-start' : 'stretch',
            gap: stepperPosition === 'left' ? 3 : 0,
            borderRadius: `${form?.theme?.borderRadius ?? 0}px`,
            overflow: 'hidden',
            ...formWidthSx(form?.theme),
            ...backgroundSx(form?.theme),
          }}
        >
          {showStepper && stepperPosition !== 'bottom' && (
            <Paper
              elevation={0}
              sx={{
                py: `${form?.theme?.formPaddingY ?? 32}px`,
                px: `${form?.theme?.formPaddingX ?? 32}px`,
                flex: stepperPosition === 'left' ? '0 0 220px' : undefined,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <FormStepper
                steps={steps}
                activeStep={currentStep}
                theme={form?.theme}
              />
            </Paper>
          )}

        <Paper
          elevation={0}
          sx={{
            py: `${form?.theme?.formPaddingY ?? 32}px`,
            px: `${form?.theme?.formPaddingX ?? 32}px`,
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          {((): JSX.Element => {
            const fieldsForStep = ((): any[] => {
              if (
                !isMultiStep ||
                !steps ||
                steps.length === 0
              ) {
                return formData;
              }
              return (formData || []).filter(
                (field: any) => field.step === currentStep,
              );
            })();

            const generatorAnswerData = ((): any => {
              const out: any = { ...(formValues || {}) };
              if (Array.isArray(formData)) {
                formData.forEach((f: any) => {
                  if (!f) return;
                  const v =
                    (f.field_name &&
                      formValues[f.field_name]) ??
                    (f.name && formValues[f.name]) ??
                    (f.id && formValues[f.id]);
                  const keys = [
                    f.field_name,
                    f.name,
                    f.id,
                  ].filter(Boolean);
                  keys.forEach((k: any) => {
                    if (
                      v !== undefined &&
                      out[k] === undefined
                    ) {
                      out[k] = v;
                    }
                  });
                });
              }
              return out;
            })();

            const compiledFieldsForStep = (() => {
              try {
                const dataObj = buildNameKeyedPayload({
                  ...(formValues || {}),
                  ...(generatorAnswerData || {}),
                });
                return (fieldsForStep || []).map(
                  (f: any) => {
                    if (!f) return f;
                    const newField = { ...f };

                    const keysToReplace = [
                      'label',
                      'text',
                      'content',
                      'description',
                      'placeholder',
                      'question',
                      'src',
                      'href',
                    ];

                    keysToReplace.forEach((k) => {
                      if (typeof newField[k] === 'string') {
                        newField[k] =
                          replaceTemplatePlaceholders(
                            newField[k],
                            dataObj,
                          );
                      }
                    });

                    if (Array.isArray(newField.options)) {
                      newField.options =
                        newField.options.map((opt: any) => {
                          if (!opt) return opt;
                          return {
                            ...opt,
                            text:
                              typeof opt.text === 'string'
                                ? replaceTemplatePlaceholders(
                                    opt.text,
                                    dataObj,
                                  )
                                : opt.text,
                            label:
                              typeof opt.label === 'string'
                                ? replaceTemplatePlaceholders(
                                    opt.label,
                                    dataObj,
                                  )
                                : opt.label,
                          };
                        });
                    }

                    if (
                      newField.data &&
                      typeof newField.data === 'object'
                    ) {
                      if (
                        typeof newField.data.href ===
                        'string'
                      ) {
                        newField.data.href =
                          replaceTemplatePlaceholders(
                            newField.data.href,
                            dataObj,
                          );
                      }
                    }

                    return newField;
                  },
                );
              } catch {
                return fieldsForStep || [];
              }
            })();

            const ansArray = ((): any[] => {
              const arr: any[] = [];
              try {
                (fieldsForStep || []).forEach((f: any) => {
                  if (!f) return;
                  const key =
                    f.field_name || f.name || f.id;
                  if (!key) return;
                  const value =
                    (formValues &&
                      (formValues[key] ??
                        formValues[f.field_name] ??
                        formValues[f.name] ??
                        formValues[f.id])) ??
                    '';
                  arr.push({ name: key, value });
                });
              } catch (e) {
                // ignore
              }
              return arr;
            })();

            if (
              !fieldsForStep ||
              fieldsForStep.length === 0
            ) {
              return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                  >
                    No fields in this step
                  </Typography>
                </Box>
              );
            }

            return (
              <FormRuntimeContext.Provider
                value={{
                  customValues,
                  crmTenantId,
                  crmJwtToken,
                  getAnswers: getWorkflowAnswers,
                  pushAiConfig: (fieldName, config) =>
                    handleChange({
                      [`${AI_OVERRIDE_PREFIX}${fieldName}`]: config,
                    }),
                  advanceStep: advanceToNextStep,
                }}
              >
              <PaymentSubmissionContext.Provider
                value={{
                  preparePayment,
                  completePayment,
                  getPaymentStatus,
                  setPaymentStatus,
                  advanceAfterPayment,
                }}
              >
                <KycSubmissionContext.Provider
                  value={{
                    prepareLead: preparePayment,
                    completeKyc,
                    getKycStatus,
                    setKycStatus,
                    getFieldValue: findFormFieldValue,
                  }}
                >
                  <EsignSubmissionContext.Provider
                    value={{
                      prepareLead: preparePayment,
                      completeEsign,
                      getEsignStatus,
                      setEsignStatus,
                    }}
                  >
                    <Box ref={formRef}>
                      <ReactFormGenerator
                        key={
                          String(currentStep) +
                          '-' +
                          (ansArray && ansArray.length > 0
                            ? JSON.stringify(ansArray)
                            : JSON.stringify(
                                generatorAnswerData,
                              ))
                        }
                        data={compiledFieldsForStep}
                        onSubmit={() => handleSubmit()}
                        onChange={handleChange}
                        form_action=""
                        form_method="POST"
                        answer_data={
                          ansArray && ansArray.length > 0
                            ? ansArray
                            : generatorAnswerData
                        }
                        hide_actions={true}
                      />
                    </Box>
                  </EsignSubmissionContext.Provider>
                </KycSubmissionContext.Provider>
              </PaymentSubmissionContext.Provider>
              </FormRuntimeContext.Provider>
            );
          })()}
        </Paper>

          {showStepper && stepperPosition === 'bottom' && (
            <Paper
              elevation={0}
              sx={{
                py: `${form?.theme?.formPaddingY ?? 32}px`,
                px: `${form?.theme?.formPaddingX ?? 32}px`,
                mt: 3,
                borderRadius: `${form?.theme?.borderRadius ?? 0}px`,
                ...backgroundSx(form?.theme),
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <FormStepper
                steps={steps}
                activeStep={currentStep}
                theme={form?.theme}
              />
            </Paper>
          )}
        </Box>
      </Container>

      <LeadContactDialog
        open={leadDialogOpen}
        fields={leadDialogFields.map((field) => ({
          name: field,
          label:
            field === 'firstName'
              ? 'First name'
              : field === 'lastName'
                ? 'Last name'
                : field[0].toUpperCase() + field.slice(1),
          required:
            field === 'firstName' ||
            REQUIRED_LEAD_FIELDS.includes(field as any),
          type:
            field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text',
        }))}
        values={leadContact}
        onValueChange={(name, value) =>
          setLeadContact((current) => ({ ...current, [name]: value }))
        }
        onCancel={cancelLeadContact}
        onConfirm={confirmLeadContact}
      />

      <OtpVerificationDialog
        open={otpDialogOpen}
        fields={otpFields}
        codes={otpCodes}
        error={otpError}
        onCodeChange={(fieldName, digitIndex, value) => {
          setOtpCodes((current) => {
            const next = [...(current[fieldName] || Array(6).fill(''))];
            next[digitIndex] = value;
            return { ...current, [fieldName]: next };
          });
          setOtpError('');
        }}
        onCancel={cancelOtpVerification}
        onConfirm={confirmOtpVerification}
      />

      <AppDialog
        open={resumePaymentDialogOpen}
        onClose={() => setResumePaymentDialogOpen(false)}
        icon={<CreditCardIcon />}
        title="Payment already completed"
        subtitle="We found a payment on this form that was already completed but not yet submitted. Please continue to finish submitting the form."
        actions={[
          {
            label: 'Dismiss',
            variant: 'secondary',
            onClick: () => setResumePaymentDialogOpen(false),
          },
          {
            label: 'Continue',
            onClick: () => {
              setResumePaymentDialogOpen(false);
              if (
                isMultiStep &&
                resumePaymentStep !== null &&
                resumePaymentStep !== currentStep
              ) {
                setCurrentStep(resumePaymentStep);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            },
          },
        ]}
      />

      <Toast
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      {/* Full-screen submit overlay: covers the form while a submit request is
          in flight (final submit or a per-step save), blocking double submits
          and stray edits. Suppressed while the OTP or lead dialogs are open —
          those are the interaction the submit is waiting on, and they stay the
          only surface the respondent can see. */}
      <Modal
        open={Boolean(submitting && !otpDialogOpen && !leadDialogOpen)}
        disableEscapeKeyDown
        aria-busy="true"
      >
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2.5,
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            cursor: 'progress',
          }}
        >
          <CircularProgress size={44} thickness={4} sx={{ color: '#fff' }} />
          <Typography
            variant="body1"
            sx={{ color: '#fff', fontWeight: 600, letterSpacing: '0.01em' }}
          >
            {submittingLabel}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default FormRenderer;
